import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FlowEditor from "@/components/flow/FlowEditor";
import { Workflow, Plus, Loader2, MoreVertical, Pencil, Copy, Trash2, Network } from "lucide-react";

export default function MaterialFlows() {
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: "list" });
  const [renaming, setRenaming] = useState(null); // {id, name}
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/flow-maps");
      setMaps(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load maps");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (view.mode === "list") load(); }, [view.mode, load]);

  const createMap = async () => {
    try {
      const { data } = await api.post("/flow-maps", { name: `Flow Map ${maps.length + 1}` });
      setView({ mode: "edit", map: data });
    } catch (e) { toast.error("Failed to create map"); }
  };

  const openMap = async (id) => {
    try {
      const { data } = await api.get(`/flow-maps/${id}`);
      setView({ mode: "edit", map: data });
    } catch (e) { toast.error("Failed to open map"); }
  };

  const duplicateMap = async (id) => {
    try { await api.post(`/flow-maps/${id}/duplicate`); toast.success("Map duplicated"); load(); }
    catch (e) { toast.error("Failed to duplicate"); }
  };

  const doRename = async () => {
    try { await api.put(`/flow-maps/${renaming.id}`, { name: renaming.name.trim() || "Untitled" }); setRenaming(null); load(); }
    catch (e) { toast.error("Failed to rename"); }
  };

  const doDelete = async () => {
    try { await api.delete(`/flow-maps/${toDelete.id}`); toast.success("Map deleted"); setToDelete(null); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  if (view.mode === "edit") {
    return <FlowEditor map={view.map} onBack={() => setView({ mode: "list" })} onNameChange={() => {}} />;
  }

  return (
    <main className="mx-auto max-w-[1400px] px-6 py-10 md:px-10 md:py-12" data-testid="material-flows-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Operations</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">Material Flows</h1>
          <p className="mt-2 text-sm text-slate-500">Map how materials move through your supply chain.</p>
        </div>
        <Button onClick={createMap} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="new-material-flow-button">
          <Plus className="mr-1 h-4 w-4" /> New Map
        </Button>
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#002FA7]" /></div>
        ) : maps.length === 0 ? (
          <div className="flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-24 text-center" data-testid="flow-empty-state">
            <Workflow className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
            <h2 className="mt-4 font-heading text-xl font-bold tracking-tight text-slate-900">No flow maps yet</h2>
            <p className="mt-2 max-w-md text-sm text-slate-500">Create your first map to visualize how materials move from suppliers through factories to customers.</p>
            <Button onClick={createMap} className="mt-6 rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="empty-new-map-button"><Plus className="mr-1 h-4 w-4" /> New Map</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {maps.map((m) => (
              <div key={m.id} className="group flex flex-col border border-slate-200 bg-white transition-colors hover:border-[#002FA7]" data-testid={`flow-map-card-${m.id}`}>
                <button onClick={() => openMap(m.id)} className="flex aspect-[4/3] items-center justify-center border-b border-slate-100 bg-slate-50 text-slate-300" data-testid={`open-map-${m.id}`}>
                  <Network className="h-10 w-10" strokeWidth={1.25} />
                </button>
                <div className="flex items-start justify-between gap-2 p-4">
                  <button onClick={() => openMap(m.id)} className="min-w-0 text-left">
                    <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 truncate">{m.name}</h3>
                    <p className="mt-1 text-xs uppercase tracking-[0.12em] text-slate-400">{m.node_count} node{m.node_count !== 1 ? "s" : ""} · {m.edge_count} link{m.edge_count !== 1 ? "s" : ""}</p>
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-slate-500" data-testid={`map-menu-${m.id}`}><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      <DropdownMenuItem onClick={() => openMap(m.id)} data-testid={`view-map-${m.id}`}>Open</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setRenaming({ id: m.id, name: m.name })} data-testid={`rename-map-${m.id}`}><Pencil className="mr-2 h-4 w-4" /> Rename</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicateMap(m.id)} data-testid={`duplicate-map-${m.id}`}><Copy className="mr-2 h-4 w-4" /> Duplicate</DropdownMenuItem>
                      <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => setToDelete(m)} data-testid={`delete-map-${m.id}`}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Rename map</AlertDialogTitle>
            <AlertDialogDescription>Give this material flow map a new name.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={renaming?.name || ""} onChange={(e) => setRenaming((r) => ({ ...r, name: e.target.value }))} className="rounded-sm" data-testid="rename-map-input" autoFocus />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-[#002FA7] hover:bg-[#002080]" onClick={doRename} data-testid="rename-map-confirm">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the map and its nodes and connections.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="delete-map-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
